import React from 'react';
import * as ToneJS from 'tone';
import { Monophonic } from 'tone/build/esm/instrument/Monophonic';
import { GaugeControl } from './Common';
import { PianoRollKeybed } from '../../rolls/PianoKeybed';
import { Note } from '../../../model/core';
import { NoteEvent } from '../../../audio/NoteActivity';
import { HorizontalNoteContainer } from '../../../model/containers';
import {
    OscConfig,
    ThreeVoiceSynth
} from '../../../audio/instruments/ThreeMonoSynth';
import { RydToggleButton } from '../../widgets/RydToggleButton';
import './Common.css';
import { normalizeClassName } from '../../../render/renderUtils';


interface SynthBlockProps {
    title: string;
    children: React.ReactNode;
    lateralLabel?: boolean;
    flexDirection?: 'row' | 'column';
}

const SynthBlock: React.FC<SynthBlockProps> = ({ title, children, lateralLabel = false, flexDirection = 'column' }) => {

    if (lateralLabel) {
        return (<div className={normalizeClassName(["ryd-instrument--row-group", "ryd-instrument--row-bordered"])}
            style={{
                margin: '1px',
                // padding: '2px',
            }}>
            <span className="ryd-instrument--lateral-label">{title}</span>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '2px', flexDirection: flexDirection }}>
                {children}
            </div>
        </div>)
    }

    return (
        <div style={{
            border: '1px solid #ccc',
            borderRadius: '2px',
            padding: '2px',
            margin: '1px',
            // minWidth: '120px'
            // display: 'grid',
            // gridTemplateColumns: '3fr',
        }}>
            {lateralLabel
                ? <span className="ryd-instrument--lateral-label">title</span>
                : <h3 style={{ textAlign: 'center', margin: '5px 0' }}>{title}</h3>
            }
            {children}
        </div>
    );
};

interface OscillatorSetupProps {
    index: number;
    update: (index: number, value: Partial<OscConfig>) => void;
    initialValue: OscConfig;
}

const OscillatorSetup: React.FC<OscillatorSetupProps> = ({ index, initialValue, update }) => {
    const [value, setValue] = React.useState<OscConfig>(initialValue);
    const oscillatorTypes: OscillatorType[] = ['sine', 'square', 'triangle', 'sawtooth'];

    return (
        <div className={normalizeClassName(["ryd-instrument--row-group", "ryd-instrument--row-bordered"])}
        style={{
            margin: '1px',
            // padding: '2px',
            }}>
            <span className="ryd-instrument--lateral-label">OSC {index}</span>

            <div style={{ display: 'flex', justifyContent: 'center', margin: '2px', flexDirection: 'row' }}>
                <RydOscillatorTypeSelector
                    value={value}
                    update={(v) => {
                        setValue({ ...value, ...v });
                        update(index, v);
                    }}
                    oscillatorTypes={oscillatorTypes}
                />

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <GaugeControl
                        label="Detune"
                        min={-12 * 100}
                        max={12 * 100}
                        update={(v) => update(index, { detune: v })} />
                    <GaugeControl
                        label="Mix"
                        min={0}
                        max={1}
                        update={(v) => update(index, { mix: v })} />
                </div>
            </div>
        </div>
    );
};

function RydOscillatorTypeSelector({
    value,
    update,
    oscillatorTypes = ['sine', 'square', 'triangle', 'sawtooth']
}: {
    value: OscConfig,
    update: (value: Partial<OscConfig>) => void,
    oscillatorTypes?: OscillatorType[]
}) {
    return (<div style={{ display: 'flex', flexDirection: 'column', margin: '2px' }}>
        {oscillatorTypes.map((type, i) => {
            return <RydToggleButton
                key={i}
                active={value.type === type}
                activeStyle={{ backgroundColor: 'orange', color: 'white' }}
                inactiveStyle={{ backgroundColor: '#ddd', color: 'black' }}
                onActivate={() => update({ type })}
                onDeactivate={() => { }}
            >
                {type.substring(0, 3).toUpperCase()}
            </RydToggleButton>;
        })}
    </div>);
}

function RydFilterTypeSelector({
    value,
    update,
    filterTypes = ['lowpass', 'highpass']
}: {
    value:ToneJS.OnePoleFilterType,
    update: (value: ToneJS.OnePoleFilterType) => void,
    filterTypes?: ToneJS.OnePoleFilterType[]
}) {
    return (<div style={{ display: 'flex', flexDirection: 'column', margin: '2px' }}>
        {filterTypes.map((type, i) => {
            return <RydToggleButton
                key={i}
                active={value === type}
                activeStyle={{ backgroundColor: 'orange', color: 'white' }}
                inactiveStyle={{ backgroundColor: '#ddd', color: 'black' }}
                onActivate={() => update(type)}
                onDeactivate={() => { }}
            >
                {type.substring(0, 3).toUpperCase()}
            </RydToggleButton>;
        })}
    </div>);
}

function SynthEnvelope({
    value,
    update
}: {
    value: ToneJS.EnvelopeOptions,
    update: (value: Partial<ToneJS.EnvelopeOptions>) => void
}) {
    return (
        <SynthBlock title="Envelope" lateralLabel={true} flexDirection='row'>
            <GaugeControl
                min={0}
                max={2}
                label="Attack"
                value={value.attack as number}
                update={v => update({ attack: v })}
            />
            <GaugeControl
                min={0.01}
                max={2}
                label="Decay"
                value={value.decay as number}
                update={v => update({ decay: v })}
            />
            <GaugeControl
                min={0}
                max={1}
                label="Sustain"
                value={value.sustain as number}
                update={v => update({ sustain: v })}
            />
            <GaugeControl
                min={0.01}
                max={4}
                label="Release"
                value={value.release as number}
                update={v => update({ release: v })}
            />
        </SynthBlock>
    );
};

function SynthFilter({
    initialValue,
    update
}: {
    initialValue: ToneJS.FilterOptions,
    update: (value: Partial<ToneJS.FilterOptions>) => void
}) {

    const [value, setValue] = React.useState<ToneJS.FilterOptions>(initialValue);
    return (
        <SynthBlock title="Filter" lateralLabel={true} flexDirection='row'>
            <GaugeControl label="Fq Cutoff"
                value={value.frequency as number}
                update={v => update({ frequency: v })}
                min={20} max={14400}
            />
            <GaugeControl label="Resonance"
                value={value.Q}
                update={v => update({ Q: v })}
                min={0.1} max={20}
            />
            <RydFilterTypeSelector
                value={value.type as ToneJS.OnePoleFilterType}
                update={v => {
                    setValue({ ...value, type: v });
                    update({ type: v });
                }}
                filterTypes={['lowpass', 'highpass']}
            />
        </SynthBlock>
    );
};

const SynthMaster: React.FC = () => {
    return (
        <SynthBlock title="Master" lateralLabel={true} flexDirection='column'>
            <GaugeControl label="Volume" />
        </SynthBlock>
    );
};

function SynthEffects({
    synth
} : {
    synth: ThreeVoiceSynth<ToneJS.SynthOptions> & Monophonic<ToneJS.SynthOptions>
}) {
    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <RydDelayControl
                value={synth.getDelaySettings()}
                update={v => synth.updateDelaySettings(v)} />
            <RydChorusControl
                value={synth.getChorusSettings()}
                update={v => synth.updateChorusSettings(v)} />
            <RydReverbControl
                value={synth.getReverbSettings()}
                update={v => synth.updateReverbSettings(v)} />
        </div>
    );
}

function RydDelayControl({
    value,
    update
}: {
    value: ToneJS.DelayOptions & {wet?: number},
    update: (value: Partial<ToneJS.DelayOptions & {wet?: number}>) => void
}) {
    // delayTime: 0.25,
    // feedback: 0.5,
    return (
        <SynthBlock title="Delay" lateralLabel={true}>
            <GaugeControl
                label="DelayTime"
                min={0}
                max={1}
                value={value.delayTime as number}
                update={v => update({ delayTime: v })}
            />
            <GaugeControl
                label="Feedback"
                min={0}
                max={1}
                value={value.maxDelay as number}
                update={v => update({ maxDelay: v })}
            />
            <GaugeControl
                label="Wet"
                min={0}
                max={1}
                value={value.wet as number}
                update={v => update({ wet: v })}
            />
        </SynthBlock>
    );
}

function RydChorusControl({
    value,
    update
}: {
    value: ToneJS.ChorusOptions,
    update: (value: Partial<ToneJS.ChorusOptions>) => void
}) {
    // frequency: 1.5,
    // delayTime: 3.5,
    // depth: 0.7,
    // feedback: 0.1,
    return (
        <SynthBlock title="Chorus" lateralLabel={true}>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '0px'}}>
                <GaugeControl
                    label="Rate"
                    min={0.1}
                    max={5}
                    value={value.frequency as number}
                    update={(v) => update({ frequency: v })} />
                <GaugeControl
                    label="DelayTime"
                    min={0.1}
                    max={5}
                    value={value.delayTime}
                    update={(v) => update({ delayTime: v })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GaugeControl
                    label="Depth"
                    min={0}
                    max={1}
                    value={value.depth}
                    update={(v) => update({ depth: v })} />
                <GaugeControl
                    label="Feedback"
                    min={0}
                    max={1}
                    value={value.feedback}
                    update={(v) => update({ feedback: v })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GaugeControl
                label="Wet"
                min={0}
                max={1}
                value={value.wet}
                update={(v) => update({ wet: v })} />
                <div></div>
                </div>
        </SynthBlock>
    );
};

function RydReverbControl({
    value,
    update
}: {
    value: {decay: number, preDelay: number, wet: number},
    update: (value: Partial<{decay: number, preDelay: number, wet: number}>) => void
}) {
    // decay: 1.5,
    // preDelay: 0.01
    return (
        <SynthBlock title="Reverb" lateralLabel={true}>
            <GaugeControl
                label="Decay"
                min={0.1}
                max={10}
                value={value.decay}
                update={v => update({ decay: v })}
            />
            <GaugeControl
                label="PreDelay"
                min={0}
                max={1}
                value={value.preDelay}
                update={v => update({ preDelay: v })}
            />
            <GaugeControl
                label="Wet"
                min={0}
                max={1}
                value={value.wet}
                update={v => update({ wet: v })}
            />
        </SynthBlock>
    );
}

export function RydInstrumentWrapper({
    children
}: {
    children: React.ReactNode
}) {
    return (
        <div style={{ padding: '2px' }} className='ryd-instrument-wrapper'>
            {children}
        </div>
    );
};

export function ThreeVoiceSynthBundle({
    synth
}: {
    synth: ThreeVoiceSynth<ToneJS.SynthOptions> & Monophonic<ToneJS.SynthOptions>
}) {

    return (
        <RydInstrumentWrapper>

            <ThreeVoiceSynthSurface synth={synth} />

            <PianoRollKeybed
                track={{
                    id: 1,
                    name: '3-Voice Synth',
                    instrument: synth,
                    length: 16,
                    color: '#ffcc00',
                    sequence: new HorizontalNoteContainer({ length: 16 }),
                    bookmarks: [],
                }}
                noteRange={{ min: 24, max: 24 + (3 * 12) }}
                showOctave={true}
                orientation={'keyboard'}
                // topOffset={0}
                tonality={undefined}
                // noteActivityObserver={NoteActivityObserver.getInstance()}
                stylingSource={undefined}
                triggerCB={(note: Note, e: NoteEvent) => {
                    if (e.event === 'noteon') {
                        synth.noteOn(note, 100);
                    } else if (e.event === 'noteoff') {
                        synth.noteOff(note);
                    }
                }}
            />
        </RydInstrumentWrapper>
    );
};

export function ThreeVoiceSynthControls({
    synth
}: {
    synth: ThreeVoiceSynth<ToneJS.SynthOptions> & Monophonic<ToneJS.SynthOptions>
}) {

    const updateVoiceSettings = (i: number, v: Partial<OscConfig>) => synth.updateVoiceSettings(i, v);

    return (<div style={{display: 'flex', flexDirection: 'row', alignItems: 'start'}}>
        <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'nowrap', flexDirection: 'column' }}>
            {/* <SynthBlock title="Oscillators"> */}
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <OscillatorSetup index={1}
                    initialValue={synth.getOscillatorSettings(1)}
                    update={updateVoiceSettings} />
                <OscillatorSetup index={2}
                    initialValue={synth.getOscillatorSettings(2)}
                    update={updateVoiceSettings} />
                <OscillatorSetup index={3}
                    initialValue={synth.getOscillatorSettings(3)}
                    update={updateVoiceSettings} />
            </div>
            {/* </SynthBlock> */}
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <SynthEnvelope
                    value={synth.getEnvelopeSettings()}
                    update={v => synth.updateEnvelopeSettings(v)} />
                <SynthFilter
                    initialValue={synth.getFilterSettings()}
                    update={v => synth.updateFilterSettings(v)} />
            </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
            <SynthEffects synth={synth}/>
            <SynthMaster />
        </div>
    </div>);
}

export function ThreeVoiceSynthSurface({
    synth
}: {
    synth: ThreeVoiceSynth<ToneJS.SynthOptions> & Monophonic<ToneJS.SynthOptions>
}) {
    return (
        <RydInstrumentWrapper>
            <ThreeVoiceSynthControls synth={synth} />
        </RydInstrumentWrapper>
    );
}